import React, { useState } from 'react';
import { getAssetPath } from '@utils/brandHelper';
import './UnitImage.css';

/**
 * Convert unit template name to slug format
 * Example: "Swordsman" -> "swordsman", "Heavy Knight" -> "heavy-knight"
 */
const getUnitSlug = (name) => {
  if (!name) return '';
  return name.toLowerCase().replace(/\s+/g, '-');
};

/**
 * Get dynamic unit image path
 * @param {string} slug - Unit slug (e.g., "swordsman")
 * @param {string} size - Icon size ('small', 'medium', 'big', 'extrabig', 'extrabig-responsive', or 'map')
 * @returns {string} Path to image
 */
const getUnitImagePath = (slug, size) => {
  let imageSize;

  if (size === 'small') {
    imageSize = 'small';
  } else if (size === 'map') {
    imageSize = 'map';
  } else if (size === 'medium') {
    imageSize = 'medium';
  } else if (size === 'extrabig' || size === 'extrabig-responsive') {
    imageSize = 'medium';
  } else {
    // Default for 'big' and any other value
    imageSize = 'medium';
  }

  return getAssetPath(`images/units/${slug}_${imageSize}.webp`);
};

/**
 * Simple component to display unit template images
 * Used in Lobby/Shop for unit templates
 *
 * @param {object} unitTemplate - Unit template object with name and visual_data
 * @param {string} size - 'small' (48px), 'medium' (60px), 'big' (70px), 'extrabig' (100px), 'extrabig-responsive' (100px max, responsive), or 'map' (default: 'small')
 * @param {string} team - Optional team class for coloring (default: 'undefined')
 */
function UnitImage({ unitTemplate, size = 'small', team = 'undefined' }) {
  const [imageError, setImageError] = useState(false);

  if (!unitTemplate) {
    console.warn('[UnitImage] No unit template provided');
    return null;
  }

  // Get unit slug and image path
  // FAIL LOUDLY: Unit template MUST have slug or name
  if (!unitTemplate.slug && !unitTemplate.name) {
    throw new Error(`[UnitImage] unit_template missing both slug and name: ${JSON.stringify(unitTemplate)}`);
  }
  const unitSlug = unitTemplate.slug || getUnitSlug(unitTemplate.name);
  const imagePath = getUnitImagePath(unitSlug, size);

  // Handle image loading error
  const handleImageError = () => {
    setImageError(true);
  };

  // Determine fallback content
  const iconFallback = unitTemplate.visual_data?.icon_fallback;
  const shouldShowImage = !imageError && unitSlug;
  const shouldShowFallback = imageError || !unitSlug;

  // Determine size class
  const sizeClass = `size-${size}`;

  // Build class names
  const classNames = [
    'unit-image',
    sizeClass,
    `team-${team}`,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classNames}
      style={{ pointerEvents: 'none' }}
    >
      {shouldShowImage ? (
        // Try to load dynamic unit image
        <img
          src={imagePath}
          alt={unitTemplate.name}
          className="unit-image-sprite"
          onError={handleImageError}
        />
      ) : iconFallback ? (
        // Show fallback emoji if available
        <span className="unit-image-letter">
          {iconFallback}
        </span>
      ) : (
        // Final fallback: show abbreviated name (more readable than just 2 chars)
        <span className="unit-image-letter" title={unitTemplate.name}>
          {(unitTemplate.name ?? '').length > 4
            ? (unitTemplate.name ?? '').slice(0, 3).toUpperCase()
            : (unitTemplate.name ?? '').toUpperCase()}
        </span>
      )}
    </div>
  );
}

export default UnitImage;
